// Shared helpers for the product catalog UI (categories grid, products grid).
// Used by TakeAway, DeliveryWithMap, and DineInOperations.
import { CatalogTone, catalogTone } from './catalogColors';

const COLOR_MAP: Record<string, string> = {
  // English
  'burger': 'bg-orange-600 hover:bg-orange-500',
  'pizza': 'bg-red-600 hover:bg-red-500',
  'salad': 'bg-emerald-600 hover:bg-emerald-500',
  'drink': 'bg-cyan-600 hover:bg-cyan-500',
  'beverage': 'bg-amber-600 hover:bg-amber-500',
  'side': 'bg-yellow-600 hover:bg-yellow-500',
  'dessert': 'bg-pink-600 hover:bg-pink-500',
  'pasta': 'bg-orange-500 hover:bg-orange-400',
  'soup': 'bg-red-500 hover:bg-red-400',
  'seafood': 'bg-blue-500 hover:bg-blue-400',
  'fish': 'bg-sky-600 hover:bg-sky-500',
  'chicken': 'bg-amber-500 hover:bg-amber-400',
  'meat': 'bg-rose-600 hover:bg-rose-500',
  'sandwich': 'bg-amber-600 hover:bg-amber-500',
  'wrap': 'bg-lime-600 hover:bg-lime-500',
  'breakfast': 'bg-yellow-500 hover:bg-yellow-400',
  'coffee': 'bg-amber-700 hover:bg-amber-600',
  'ice cream': 'bg-purple-500 hover:bg-purple-400',
  'sushi': 'bg-pink-500 hover:bg-pink-400',
  'appetizer': 'bg-orange-500 hover:bg-orange-400',
  'snack': 'bg-yellow-500 hover:bg-yellow-400',
  'wine': 'bg-purple-600 hover:bg-purple-500',
  'beer': 'bg-amber-500 hover:bg-amber-400',
  'cocktail': 'bg-pink-500 hover:bg-pink-400',
  // Greek
  'μπέργκερ': 'bg-orange-600 hover:bg-orange-500',
  'πίτσα': 'bg-red-600 hover:bg-red-500',
  'σαλάτ': 'bg-emerald-600 hover:bg-emerald-500',
  'ποτ': 'bg-cyan-600 hover:bg-cyan-500',
  'αναψυκτικ': 'bg-cyan-600 hover:bg-cyan-500',
  'ρόφημ': 'bg-amber-600 hover:bg-amber-500',
  'συνοδευτικ': 'bg-yellow-600 hover:bg-yellow-500',
  'ορεκτικ': 'bg-orange-500 hover:bg-orange-400',
  'επιδόρπ': 'bg-pink-600 hover:bg-pink-500',
  'γλυκ': 'bg-pink-600 hover:bg-pink-500',
  'ζυμαρικ': 'bg-orange-500 hover:bg-orange-400',
  'σούπ': 'bg-red-500 hover:bg-red-400',
  'θαλασσιν': 'bg-blue-500 hover:bg-blue-400',
  'ψάρ': 'bg-sky-600 hover:bg-sky-500',
  'κοτόπουλ': 'bg-amber-500 hover:bg-amber-400',
  'κρέας': 'bg-rose-600 hover:bg-rose-500',
  'κρεατικ': 'bg-rose-600 hover:bg-rose-500',
  'σάντουιτς': 'bg-amber-600 hover:bg-amber-500',
  'τοστ': 'bg-amber-600 hover:bg-amber-500',
  'πρωιν': 'bg-yellow-500 hover:bg-yellow-400',
  'καφ': 'bg-amber-700 hover:bg-amber-600',
  'παγωτ': 'bg-purple-500 hover:bg-purple-400',
  'σνακ': 'bg-yellow-500 hover:bg-yellow-400',
  'κρασ': 'bg-purple-600 hover:bg-purple-500',
  'μπύρ': 'bg-amber-500 hover:bg-amber-400',
  'μπίρ': 'bg-amber-500 hover:bg-amber-400',
  'υπηρεσί': 'bg-slate-500 hover:bg-slate-400',
  'υπηρεσιε': 'bg-slate-500 hover:bg-slate-400',
};

const DEFAULT_COLORS = [
  'bg-slate-500 hover:bg-slate-400',
  'bg-teal-600 hover:bg-teal-500',
  'bg-indigo-600 hover:bg-indigo-500',
  'bg-violet-600 hover:bg-violet-500',
  'bg-fuchsia-600 hover:bg-fuchsia-500',
  'bg-sky-600 hover:bg-sky-500',
];

export function getCategoryColor(name: string, index: number): string {
  const n = name.toLowerCase();
  for (const [key, color] of Object.entries(COLOR_MAP)) {
    if (n.includes(key)) return color;
  }
  return DEFAULT_COLORS[index % DEFAULT_COLORS.length];
}

// ── Χρώμα που όρισε ο διαχειριστής (migration 264) ─────────────────────────────
// Η κατηγορία μπορεί να έχει ρητό τόνο (category.color)· αλλιώς ισχύει ο αυτόματος κανόνας ακριβώς
// όπως πριν. ΜΗΝ αλλάξεις το getCategoryColor: χωρίς χρώμα όλα πρέπει να μένουν ίδια.

/** Ο τόνος του αυτόματου κανόνα ('bg-amber-600 …' → 'amber'): από το όνομα, αλλιώς κυκλικά με τη θέση. */
export function autoCategoryTone(name: string, index: number): CatalogTone {
  return catalogTone(getCategoryColor(name, index).split('-')[1]) ?? 'slate';
}

/** Ο τόνος της κατηγορίας στο ταμείο: ο ρητός του διαχειριστή, αλλιώς ο αυτόματος. */
export function categoryToneOf(category: { name: string; color?: string | null }, index: number): CatalogTone {
  return catalogTone(category.color) ?? autoCategoryTone(category.name, index);
}

// Κλάσεις Tailwind ανά τόνο για τα κλασικά πλακίδια. ΚΥΡΙΟΛΕΚΤΙΚΕΣ (όχι `bg-${tone}-600`): το
// Tailwind παράγει μόνο ό,τι βλέπει γραμμένο στον κώδικα.
const TONE_CLASSES: Record<CatalogTone, string> = {
  slate: 'bg-slate-500 hover:bg-slate-400',
  red: 'bg-red-600 hover:bg-red-500',
  orange: 'bg-orange-600 hover:bg-orange-500',
  amber: 'bg-amber-600 hover:bg-amber-500',
  yellow: 'bg-yellow-600 hover:bg-yellow-500',
  lime: 'bg-lime-600 hover:bg-lime-500',
  emerald: 'bg-emerald-600 hover:bg-emerald-500',
  teal: 'bg-teal-600 hover:bg-teal-500',
  cyan: 'bg-cyan-600 hover:bg-cyan-500',
  sky: 'bg-sky-600 hover:bg-sky-500',
  blue: 'bg-blue-600 hover:bg-blue-500',
  indigo: 'bg-indigo-600 hover:bg-indigo-500',
  violet: 'bg-violet-600 hover:bg-violet-500',
  purple: 'bg-purple-600 hover:bg-purple-500',
  fuchsia: 'bg-fuchsia-600 hover:bg-fuchsia-500',
  pink: 'bg-pink-600 hover:bg-pink-500',
  rose: 'bg-rose-600 hover:bg-rose-500',
};

/** Κλάσεις φόντου ενός τόνου ('amber' → 'bg-amber-600 hover:bg-amber-500'). */
export function toneClasses(tone: CatalogTone): string {
  return TONE_CLASSES[tone];
}

/** Κλασικό πλακίδιο: οι κλάσεις του ρητού τόνου, αλλιώς ΑΚΡΙΒΩΣ ό,τι έδινε το getCategoryColor. */
export function categoryColorClasses(category: { name: string; color?: string | null }, index: number): string {
  const tone = catalogTone(category.color);
  return tone ? TONE_CLASSES[tone] : getCategoryColor(category.name, index);
}

export function getGridClass(cols: number): string {
  const map: Record<number, string> = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
    7: 'grid-cols-7',
    8: 'grid-cols-8',
  };
  return map[cols] || 'grid-cols-4';
}

// Font size scales with column count (fewer columns = bigger text)
export function getCategoryFontSize(cols: number): string {
  if (cols <= 2) return 'text-2xl';
  if (cols <= 3) return 'text-xl';
  if (cols <= 4) return 'text-base';
  if (cols <= 5) return 'text-sm';
  return 'text-xs';
}

export function getProductFontSize(cols: number): string {
  if (cols <= 2) return 'text-xl';
  if (cols <= 3) return 'text-lg';
  if (cols <= 4) return 'text-base';
  if (cols <= 5) return 'text-sm';
  return 'text-xs';
}

export function getProductPriceFontSize(cols: number): string {
  if (cols <= 2) return 'text-2xl';
  if (cols <= 3) return 'text-xl';
  if (cols <= 4) return 'text-lg';
  if (cols <= 5) return 'text-base';
  return 'text-sm';
}

export function getCategoryMinHeight(cols: number): string {
  if (cols <= 2) return 'min-h-[180px]';
  if (cols <= 3) return 'min-h-[150px]';
  if (cols <= 4) return 'min-h-[120px]';
  if (cols <= 5) return 'min-h-[100px]';
  return 'min-h-[80px]';
}

export function getProductMinHeight(cols: number): string {
  if (cols <= 2) return 'min-h-[160px]';
  if (cols <= 3) return 'min-h-[140px]';
  if (cols <= 4) return 'min-h-[120px]';
  if (cols <= 5) return 'min-h-[100px]';
  return 'min-h-[80px]';
}
